import { useQuery } from "@tanstack/react-query";
import { apiFetch, apiUrl } from "../lib/api";
import { buildCreditBreakdown, type CreditBreakdown } from "../lib/credit-buckets";
import type { PlanSlug } from "../lib/plans";
import { getQueryClient } from "../lib/query-client";

type CreditsResponse = {
  plan: PlanSlug;
  planName: string;
  balance: number;
  subscriptionBalance: number;
  packBalance: number;
  packSpendable: boolean;
  packExpirations: { credits: number; total?: number; expiresAt: string }[];
  employeeLimit: number;
  companyLimit: number;
  monthlyCredits: number;
  interval?: "month" | "year" | null;
  nextResetAt?: string | null;
  subscriptionId?: string | null;
  /** An open trial; `plan` above stays the paid tier. */
  trial?: PlanTrial | null;
  trialNudge?: TrialNudge | null;
};

/** A tier unlocked for a limited time: the signup or launch trial. */
export type PlanTrial = { plan: PlanSlug; planName: string; endsAt: string };

/**
 * Beyond the open trial: the banner once a signup or launch trial has ended,
 * and the win-back offer while it applies. Wording comes from the admin's
 * Settings → Trial lifecycle, already filled in.
 */
export type TrialNudge = {
  planName: string;
  endsAt: string;
  ended: boolean;
  /** Applied for the account at checkout; shown so they know. */
  offer: { label: string; until: string | null } | null;
  banner: {
    headline: string;
    body: string;
    buttonLabel: string;
    snoozeDays: number;
    lost: string[];
    activity: { label: string; value: string }[];
  } | null;
};

/** The account's live credit balances and plan limits. */
export type AccountUsage = {
  /** Total spendable credits (subscription allowance + purchased packs). */
  balance: number;
  /** Credits left from this cycle's plan allowance. */
  subscriptionBalance: number;
  /** Credits left from purchased top-up packs. */
  packBalance: number;
  packSpendable: boolean;
  /** Unspent add-on lots, ordered by nearest expiration. */
  packExpirations: { credits: number; total: number; expiresAt: string }[];
  /** The plan's monthly credit allowance (meter denominator). */
  monthlyCredits: number;
  /** Included allowance for the active billing period (12x on annual plans). */
  creditAllowance: number;
  /** When the plan pool next resets to its allowance, or `null`. */
  nextResetAt: string | null;
  employeeLimit: number;
  companyLimit: number;
  planName: string;
};

export type AccountPlanState = {
  /**
   * The tier features and limits follow, or `null` while still loading. During
   * a trial that is the trial tier, not the paid one.
   */
  plan: PlanSlug | null;
  /**
   * The tier actually paid for. Differs from `plan` only during a trial — use it
   * wherever the question is about billing (what to sell, whether packs apply).
   */
  paidPlan: PlanSlug | null;
  /** The open trial, if any. */
  trial: PlanTrial | null;
  /** The ended-trial banner and the win-back offer, if any. */
  trialNudge: TrialNudge | null;
  /** Whether the account is on the Free plan (gates the PDF watermark, etc.). */
  isFree: boolean;
  /** Live balances + limits, or `null` while loading / on error. */
  usage: AccountUsage | null;
  /** The unified credit-bucket breakdown, or `null` while loading / on error. */
  breakdown: CreditBreakdown | null;
  /**
   * The live Polar plan subscription, or `null` on Free / while loading. Its
   * presence is what makes picking a plan a *switch* of this subscription rather
   * than a new checkout — see `usePlanChange`.
   */
  subscriptionId: string | null;
  isLoading: boolean;
};

/**
 * The account's resolved billing plan, backed by React Query so it's fetched
 * once and shared. Billing is account-level, so this is the same plan across
 * every company the user owns. Used to decide whether client-rendered document
 * PDFs get the Free-plan watermark.
 *
 * Defaults `isFree` to `false` while loading so paid users never briefly stamp a
 * watermark on their downloads; the backend applies the same stamp on the email
 * path, so a Free account that races the fetch is still covered there.
 */
/**
 * Shared options so a route loader and this hook resolve to one cache entry.
 *
 * The Next.js dashboard layout fetched the credits server-side and seeded
 * `["account-plan"]` via `HydrationBoundary`, so the sidebar, upgrade card and
 * every plan-gated control rendered the correct tier on first paint instead of
 * flashing "Free". The workspace route loader now does that by awaiting these
 * same options before the shell renders.
 */
export const accountPlanQuery = {
  queryKey: ["account-plan"] as const,
  queryFn: async (): Promise<CreditsResponse | null> => {
    const res = await apiFetch(apiUrl("/api/billing/credits"));
    if (!res.ok) return null;
    return (await res.json()) as CreditsResponse;
  },
  staleTime: 5 * 60 * 1000,
  retry: false,
};

/**
 * Refetch the account's credit balance after an action that may have spent (or
 * refunded) credits, so the sidebar meter and billing pages reflect it without a
 * page reload. Generation debits happen server-side — in the worker for queued
 * documents, in the route for synchronous AI calls — so the client has to ask.
 */
export function refreshAccountPlan(): Promise<void> {
  return getQueryClient().invalidateQueries({ queryKey: accountPlanQuery.queryKey });
}

export function useAccountPlan(): AccountPlanState {
  const { data, isLoading } = useQuery(accountPlanQuery);

  const paidPlan = data?.plan ?? null;
  const trial = data?.trial ?? null;
  // A grant whose end has passed since the fetch no longer counts; the server
  // has already stopped honouring it.
  const plan = trial && new Date(trial.endsAt) > new Date() ? trial.plan : paidPlan;
  const usage: AccountUsage | null = data
    ? {
        balance: data.balance,
        subscriptionBalance: data.subscriptionBalance,
        packBalance: data.packBalance,
        packSpendable: data.packSpendable,
        // Older backends may omit `total`; fall back to remaining so a lot never
        // reads as more-than-100% used.
        packExpirations: data.packExpirations.map((lot) => ({
          credits: lot.credits,
          total: lot.total ?? lot.credits,
          expiresAt: lot.expiresAt,
        })),
        monthlyCredits: data.monthlyCredits,
        creditAllowance: data.monthlyCredits * (data.interval === "year" ? 12 : 1),
        nextResetAt: data.nextResetAt ?? null,
        employeeLimit: data.employeeLimit,
        companyLimit: data.companyLimit,
        planName: data.planName,
      }
    : null;

  const breakdown: CreditBreakdown | null = usage
    ? buildCreditBreakdown({
        subscriptionBalance: usage.subscriptionBalance,
        creditAllowance: usage.creditAllowance,
        packSpendable: usage.packSpendable,
        packExpirations: usage.packExpirations,
        nextResetAt: usage.nextResetAt,
      })
    : null;

  return {
    plan,
    paidPlan,
    trial: plan === paidPlan ? null : trial,
    trialNudge: data?.trialNudge ?? null,
    isFree: plan === "free",
    usage,
    breakdown,
    subscriptionId: data?.subscriptionId ?? null,
    isLoading,
  };
}
